import React from 'react';
import Button from 'components/commercetools-ui/atoms/button';
import Image from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import WarningIcon from 'components/icons/warning-icon';
import { useFormat } from 'helpers/hooks/useFormat';
import { Reference } from 'types/reference';
import Markdown from '../markdown';
import NotFoundBackground from '../product/product-list/images/not-found-background';

export type NotFoundProps = {
  title: string;
  markdown: string;
  unsafeMarkdown: boolean;
  ctaLabel?: string;
  ctaReference?: Reference;
  backgroundColor?: string;
  textAlign?: 'text-start' | 'text-center' | 'text-end';
  textJustify?: 'start' | 'center' | 'end';
};

const NotFound: React.FC<NotFoundProps> = ({
  title,
  markdown,
  unsafeMarkdown,
  ctaLabel,
  ctaReference,
  backgroundColor,
  textAlign = 'text-center',
  textJustify = 'center',
}) => {
  const { formatMessage } = useFormat({ name: 'common' });

  return (
    <div className="relative h-auto md:pt-58 xl:pt-0">
      <div
        className={`items-${textJustify} flex w-full flex-row gap-36 p-18 pb-60 text-gray-700 md:p-50 xl:mx-auto xl:max-w-[1200px] xl:px-0`}
        style={{
          backgroundColor: backgroundColor,
        }}
      >
        <div className="absolute left-0 top-0 z-0 flex size-full items-center justify-center">
          <NotFoundBackground className="opacity-10" />
        </div>
        <div className="z-10 w-fit">
          <WarningIcon />
        </div>
        <div className={`${textAlign} z-10`}>
          <h1 className="text-32">
            {title ? title : formatMessage({ id: 'page.notFound', defaultMessage: 'Page not found' })}
          </h1>
          <div className="-mt-24 flex">
            {markdown ? (
              <Markdown unsafeMarkdown={unsafeMarkdown} markdown={markdown} className="prose max-w-full no-underline" />
            ) : (
              formatMessage({
                id: 'page.notFound.desc',
                defaultMessage:
                  "The page you were looking for couldn't be found. It may have been deleted, renamed, or doesn't exist.",
              })
            )}
          </div>
          {ctaLabel && (
            <Link link={ctaReference ? ctaReference : '/'} className="z-10">
              <Button variant="primary" className="mt-28 px-48 py-12 text-16 font-medium">
                {ctaLabel ? ctaLabel : formatMessage({ id: 'page.notFound.cta', defaultMessage: 'Go back home' })}
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotFound;
